import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import { sidebarDataAdmin, sidebarDataStudent } from "../../data/SidebarData";
import useCurrentPath from "../../hooks/useCurrentPath";
import Modal from "./Modal";
import Storage from "../../Pages/Admin/Storage";
import { useAuth } from "../../Context/AuthContext";
import { dashboardUrls, studentUrls } from "../../Api/Auth/AppUrls";
import { checkStudentRouteVisibility } from "../../utils/findStudentRouteBySlug";

const Sidebar = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLabels, setShowLabels] = useState(true); // Controls delayed text appearance
  const pathname = useCurrentPath();
  const { userData, categoryData } = useAuth();

  // Mapping of sidebar items to their Civil and Mechanical route pairs
  const sidebarItemRoutePairs = {
    [studentUrls.liveClass]: {
      civil: studentUrls.liveClass,
      mechanical: studentUrls.liveClassMechanical,
    },
    [studentUrls.studentQuizBankCivil]: {
      civil: studentUrls.studentQuizBankCivil,
      mechanical: studentUrls.studentQuizBankMechanical,
    },
    [studentUrls.studentSimulatorCivil]: {
      civil: studentUrls.studentSimulatorCivil,
      mechanical: studentUrls.studentSimulatorMechanical,
    },
    [studentUrls.studentSubscriptionMaterials]: {
      civil: studentUrls.studentSubscriptionMaterials,
      mechanical: studentUrls.studentSubscriptionMaterialsMechanical,
    },
    [studentUrls.studentPracticeHub]: {
      civil: studentUrls.studentPracticeHub,
      mechanical: studentUrls.studentPracticeHubMechanical,
    },
    [studentUrls.studentPaidModelTest]: {
      civil: studentUrls.studentPaidModelTest,
      mechanical: studentUrls.studentPaidModelTestMechanical,
    },
    [studentUrls.studentTutoring]: {
      civil: studentUrls.studentTutoring,
      mechanical: studentUrls.studentTutoringMechanical,
    },
  };

  // Filter sidebar data based on category visibility
  const filteredSidebarData = useMemo(() => {
    const baseSidebarData =
      userData?.roleId === 2
        ? sidebarDataStudent
        : userData?.roleId === 6
        ? sidebarDataAdmin.filter((item) => item.to !== dashboardUrls.admin)
        : sidebarDataAdmin;

    // For student role, filter items based on category visibility
    if (userData?.roleId === 2 && categoryData) {
      return baseSidebarData.filter((item) => {
        // If item has route pairs (Civil/Mechanical variants), check visibility
        const routePair = sidebarItemRoutePairs[item.to];
        if (routePair) {
          const isVisibleCivil = checkStudentRouteVisibility(
            categoryData,
            routePair.civil
          );
          const isVisibleMechanical = checkStudentRouteVisibility(
            categoryData,
            routePair.mechanical
          );
          // Show item if at least one category is visible
          return isVisibleCivil || isVisibleMechanical;
        }
        // For items without route pairs (like Dashboard), always show
        return true;
      });
    }

    // For admin roles, return as is
    return baseSidebarData;
  }, [userData?.roleId, categoryData]);

  const sidebarData = filteredSidebarData;

  useEffect(() => {
    if (sidebarOpen) {
      const timer = setTimeout(() => setShowLabels(true), 200); // Delay text by 200ms
      return () => clearTimeout(timer);
    } else {
      setShowLabels(false); // Hide text immediately when collapsing
    }
  }, [sidebarOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };

    // Run on mount to check initial window size
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      className={`${
        sidebarOpen ? "w-[220px]" : "w-16"
      } bg-white border-r h-auto shadow-lg transition-all duration-300 ease-in-out relative flex flex-col pt-24`}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="absolute top-[140px] right-[-16px] bg-gray-800 text-white w-8 h-8 flex items-center justify-center rounded-full shadow-md z-10 hover:scale-105 transition-all duration-200"
      >
        <FontAwesomeIcon icon={sidebarOpen ? faChevronLeft : faChevronRight} />
      </button>

      {/* Sidebar Content */}
      <nav className="flex-1 overflow-hidden">
        <ul className="space-y-2.5 pt-11">
          {sidebarData.map(({ to, icon, label, hasNotification }) => {
            const isCurrentlyActive = to === pathname;
            const isStorage = label === "Storage";
            return (
              <li key={to}>
                <Link
                  to={to}
                  onClick={() => {
                    if (isStorage) {
                      setModalOpen(true);
                    }
                  }}
                  className={`flex items-center p-2 hover:bg-gray-200 rounded-md transition-all duration-200 ease-in-out ${
                    isCurrentlyActive && "bg-primary"
                  } ${isCurrentlyActive && "text-white hover:text-black"}`}
                >
                  {/* Fixed Icon Size */}
                  <div className={`w-10 flex justify-center `}>
                    <FontAwesomeIcon icon={icon} className="text-lg" />
                  </div>

                  {/* Delayed Label Appearance */}
                  {sidebarOpen && (
                    <span
                      className={`ml-2 w-[160px] transition-opacity duration-200 ${
                        showLabels ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {label}
                    </span>
                  )}

                  {/* Notification Badge */}
                  {hasNotification && sidebarOpen && (
                    <span className="ml-auto text-xs text-red-500">1</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <Modal
        isOpen={isModalOpen}
        title={"Storage Manager"}
        // onConfirm={handleSubmit}
        onCancel={() => {
          setModalOpen(false);
        }}
        footer={false}
      >
        <Storage />
      </Modal>
    </div>
  );
};

export default Sidebar;
